import {intersect} from '@benchling/js-utils';

import {Action, ItemType} from 'auth/PolicyConstants';
import {
  ActionOptionGroupNode,
  ActionOptionNode,
  ConditionOptionNode,
} from 'graphql-client/__generated/graphqlGlobalTypes';

export interface ActionItemType {
  action: Action;
  itemType: ItemType;
}

export interface ActionRowData {
  name: string;
  description: string | null;
  level: number;
  disabled: boolean;
  actionItemTypes: Array<ActionItemType>;
  conditionOptions: Array<ConditionOptionNode>;
}

function expandActionOption(
  itemType: ItemType,
  actionOption: ActionOptionNode | ActionOptionGroupNode,
  level: number
): Array<ActionRowData> {
  // eslint-disable-next-line benchlint/no-in-operator
  if (!('actionOptions' in actionOption)) {
    const baseActionOption: ActionOptionNode = actionOption;
    return [
      {
        name: baseActionOption.name,
        disabled: Boolean(baseActionOption.disabled),
        description: baseActionOption.description,
        actionItemTypes: [
          {action: baseActionOption.action, itemType: baseActionOption.itemTypeOverride || itemType},
        ],
        conditionOptions: baseActionOption.conditionOptions,
        level,
      },
    ];
  }
  const actionOptionGroup: ActionOptionGroupNode = actionOption;
  const rowDatas = getActionRowDatas(itemType, actionOptionGroup.actionOptions, level + 1);
  return [
    {
      name: actionOptionGroup.name,
      disabled: rowDatas.some((actionRowData) => actionRowData.disabled),
      description: actionOptionGroup.description,
      // Include all children's action item types
      actionItemTypes: ([] as Array<ActionItemType>).concat(
        ...rowDatas.map((rowData) => rowData.actionItemTypes)
      ),
      // Include the intersection of children's condition options
      conditionOptions: intersect(
        rowDatas.map((rowData) => rowData.conditionOptions),
        (conditionOption: ConditionOptionNode) => conditionOption.condition
      ),
      level,
    },
    ...rowDatas,
  ];
}

export default function getActionRowDatas(
  itemType: ItemType,
  actionOptions: Array<ActionOptionNode | ActionOptionGroupNode>,
  level?: number
): Array<ActionRowData> {
  if (!level) {
    level = 0;
  }
  return ([] as Array<ActionRowData>).concat(
    ...actionOptions.map((actionOption) => expandActionOption(itemType, actionOption, level!))
  );
}
